import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Users, Calendar, CalendarDays, Info, Plus, Trash2, Search, Edit2, Power, ChevronLeft, ChevronRight, FileText, Download } from 'lucide-react';
import { MinistriesService, type Ministerio, type Evento, type ReporteAsistencia, type EventoMovimiento } from '../../services/ministries/ministries.service';
import { parseFechaLocal } from '../../utils/date.utils';
import { MembershipsService } from '../../services/memberships/memberships.service';
import { CreateMemberDrawer } from '../memberships/CreateMemberDrawer';
import { NotificationToast } from '../ui/NotificationToast';
import { CreateEventoDrawer } from './CreateEventoDrawer';
import { ProgramacionService, type ProgramacionAsignacion } from '../../services/programacion/programacion.service';
import { EstadoSeguimientoBadge } from '../programacion/EstadoSeguimientoBadge';
import { formatFechaLocal } from '../../utils/date.utils';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { usePermissions } from '../../hooks/usePermissions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

type TabType = 'general' | 'integrantes' | 'eventos' | 'programacion' | 'reporte';

interface MinisterioDetailsDrawerProps {
  isOpen: boolean;
  ministerio: Ministerio | null;
  onClose: () => void;
  onEdit?: (m: Ministerio) => void;
  onToggleStatus?: (m: Ministerio) => Promise<void> | void;
  onDelete?: (id: string, nombre: string) => Promise<void> | void;
  onRefresh?: () => void;
}

export const MinisterioDetailsDrawer: React.FC<MinisterioDetailsDrawerProps> = ({
  isOpen,
  ministerio,
  onClose,
  onEdit,
  onToggleStatus,
  onDelete,
  onRefresh,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [currentMinisterio, setCurrentMinisterio] = useState<Ministerio | null>(null);
  const [miembrosRegistrados, setMiembrosRegistrados] = useState<Array<{ creadoPorId: string; nombre: string; count: number; miembros: Array<{ id: string; nombreCompleto: string; creadoEn: string }> }> | null>(null);

  const [isCreateEventoOpen, setIsCreateEventoOpen] = useState(false);
  const [createEventoDate, setCreateEventoDate] = useState<string | undefined>(undefined);
  const [isCreateMemberOpen, setIsCreateMemberOpen] = useState(false);
  const [editingEvento, setEditingEvento] = useState<Evento | null>(null);
  const [selectedEvento, setSelectedEvento] = useState<Evento | null>(null);

  const [newMembresiaId, setNewMembresiaId] = useState('');
  const [newRol, setNewRol] = useState('');
  const [isSubmittingIntegrante, setIsSubmittingIntegrante] = useState(false);
  const [isRemovingIntegranteId, setIsRemovingIntegranteId] = useState<string | null>(null);

  // Buscador de integrantes
  const [integranteSearch, setIntegranteSearch] = useState('');
  const [integranteResults, setIntegranteResults] = useState<Array<{ id: string; nombres: string; apellidos: string; email: string }>>([]);
  const [integranteLoading, setIntegranteLoading] = useState(false);
  const [showIntegranteResults, setShowIntegranteResults] = useState(false);
  const [selectedIntegranteName, setSelectedIntegranteName] = useState('');
  const integranteSearchRef = useRef<HTMLDivElement>(null);

  const [asistenciaMap, setAsistenciaMap] = useState<Record<string, boolean>>({});
  const [isSavingAsistencia, setIsSavingAsistencia] = useState(false);
  const [isLoadingAsistencia, setIsLoadingAsistencia] = useState(false);

  // Reporte de asistencia
  const [reporteSubTab, setReporteSubTab] = useState<'registros' | 'asistencia'>('registros');
  const [asistenciaReporte, setAsistenciaReporte] = useState<ReporteAsistencia | null>(null);
  const [asistenciaDesde, setAsistenciaDesde] = useState('');
  const [asistenciaHasta, setAsistenciaHasta] = useState('');
  const [isLoadingAsistenciaReporte, setIsLoadingAsistenciaReporte] = useState(false);

  // Finanzas del evento seleccionado
  const [movimientosEvento, setMovimientosEvento] = useState<EventoMovimiento[]>([]);
  const [isLoadingMovimientos, setIsLoadingMovimientos] = useState(false);
  const [nuevoMovimiento, setNuevoMovimiento] = useState<{ tipo: 'ingreso' | 'gasto'; monto: string; descripcion: string; fecha: string }>({
    tipo: 'gasto',
    monto: '',
    descripcion: '',
    fecha: '',
  });
  const [isSavingMovimiento, setIsSavingMovimiento] = useState(false);

  // Calendario
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  // Asignaciones futuras de programación semanal para este ministerio.
  // null = aún no se sabe (no cargado o sin acceso).
  const [asignacionesProg, setAsignacionesProg] = useState<ProgramacionAsignacion[] | null>(null);

  const { hasPermission } = usePermissions();
  const canViewReporte = hasPermission('Ministerios', 'reporte');
  const canEditEventos = hasPermission('Eventos', 'editar');

  const eventosPorFecha = useMemo(() => {
    const map: Record<string, Evento[]> = {};
    (currentMinisterio?.eventos || []).forEach((ev) => {
      const key = ev.fecha.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [currentMinisterio?.eventos]);

  const proximoEvento = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const eventos = (currentMinisterio?.eventos || [])
      .slice()
      .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    const futuro = eventos.find((ev) => parseFechaLocal(ev.fecha).getTime() >= hoy.getTime());
    return futuro || eventos[eventos.length - 1] || null;
  }, [currentMinisterio?.eventos]);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    if (isOpen && ministerio) {
      setCurrentMinisterio(ministerio);
      setActiveTab('general');
      setIsCreateMemberOpen(false);
      setIsCreateEventoOpen(false);
      setCreateEventoDate(undefined);
      setEditingEvento(null);
      setSelectedEvento(null);
      const timer = setTimeout(() => setIsVisible(true), 10);
      // Cargar datos completos (incluye eventos e integrantes)
      MinistriesService.getById(ministerio.id)
        .then((full) => setCurrentMinisterio(full))
        .catch((err) => console.error('Error cargando detalle del ministerio:', err));

      // Cargar conteo de miembros registrados por cada persona en el ministerio
      MembershipsService.countByRegistrador(ministerio.id)
        .then((data) => setMiembrosRegistrados(data))
        .catch((err) => console.error('Error cargando conteo de miembros:', err));

      // Cargar asignaciones futuras de programación semanal del ministerio
      // (permiso 'ver' de Programación Semanal o ser líder del ministerio)
      setAsignacionesProg(null);
      ProgramacionService.getAsignacionesPorMinisterio(ministerio.id)
        .then((data) => setAsignacionesProg(data))
        .catch((err) => {
          // 403 (sin permiso ni liderazgo) u otro error: se muestra la pestaña vacía
          if (!(err instanceof Error && err.message === 'forbidden')) {
            console.error('Error cargando programación del ministerio:', err);
          }
          setAsignacionesProg([]);
        });
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, ministerio]);

  useEffect(() => {
    if (selectedEvento && currentMinisterio) {
      loadAsistencias(selectedEvento.id);
      loadMovimientos(selectedEvento.id);
    }
  }, [selectedEvento]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      setSelectedEvento(null);
      setIsCreateEventoOpen(false);
      setCreateEventoDate(undefined);
      setEditingEvento(null);
      setIsCreateMemberOpen(false);
      setNewMembresiaId('');
      setNewRol('');
      setIntegranteSearch('');
      setIntegranteResults([]);
      setSelectedIntegranteName('');
      setShowIntegranteResults(false);
    }, 300);
  };

  const refreshMinisterio = async () => {
    if (!currentMinisterio?.id) return;
    try {
      const updated = await MinistriesService.getById(currentMinisterio.id);
      setCurrentMinisterio(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const showToast = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotificationConfig({ title, message, type });
    setShowNotification(true);
  };

  // Click outside para cerrar resultados de búsqueda de integrante
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (integranteSearchRef.current && !integranteSearchRef.current.contains(event.target as Node)) {
        setShowIntegranteResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce búsqueda de integrante
  useEffect(() => {
    if (!integranteSearch || integranteSearch.length < 2) {
      setIntegranteResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIntegranteLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.memberships.list}?search=${encodeURIComponent(integranteSearch)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (response.ok) {
          const result = await response.json();
          const data = result.data?.data || result.data || [];
          // Excluir integrantes ya existentes
          const existentes = new Set((currentMinisterio?.integrantes || []).map((i) => i.membresiaId));
          setIntegranteResults(
            data
              .filter((m: any) => !existentes.has(m.id))
              .map((m: any) => ({
                id: m.id,
                nombres: m.nombres,
                apellidos: m.apellidos,
                email: m.email,
              }))
          );
          setShowIntegranteResults(true);
        }
      } catch (err) {
        console.error('Error buscando integrante:', err);
      } finally {
        setIntegranteLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [integranteSearch, currentMinisterio?.integrantes]);

  const handleAddIntegrante = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentMinisterio?.id || !newMembresiaId.trim()) return;
    setIsSubmittingIntegrante(true);
    try {
      await MinistriesService.addIntegrante(currentMinisterio.id, {
        membresiaId: newMembresiaId.trim(),
        rol: newRol.trim() || undefined,
      });
      showToast('Integrante agregado', 'El integrante fue agregado exitosamente.', 'success');
      setNewMembresiaId('');
      setNewRol('');
      setIntegranteSearch('');
      setSelectedIntegranteName('');
      setIntegranteResults([]);
      await refreshMinisterio();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al agregar integrante';
      showToast('Error', msg, 'error');
    } finally {
      setIsSubmittingIntegrante(false);
    }
  };

  const handleRemoveIntegrante = async (membresiaId: string) => {
    if (!currentMinisterio?.id) return;
    setIsRemovingIntegranteId(membresiaId);
    try {
      await MinistriesService.removeIntegrante(currentMinisterio.id, membresiaId);
      showToast('Integrante removido', 'El integrante fue removido exitosamente.', 'success');
      await refreshMinisterio();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al remover integrante';
      showToast('Error', msg, 'error');
    } finally {
      setIsRemovingIntegranteId(null);
    }
  };

  const loadAsistencias = async (eventoId: string) => {
    setIsLoadingAsistencia(true);
    try {
      const asistencias = await MinistriesService.getAsistencia(eventoId);
      const map: Record<string, boolean> = {};
      asistencias?.forEach((a) => {
        map[a.membresiaId] = a.asistio;
      });
      setAsistenciaMap(map);
    } catch (error) {
      console.error(error);
      setAsistenciaMap({});
    } finally {
      setIsLoadingAsistencia(false);
    }
  };

  const handleToggleAsistencia = (membresiaId: string) => {
    setAsistenciaMap((prev) => ({ ...prev, [membresiaId]: !prev[membresiaId] }));
  };

  const handleSaveAsistencia = async () => {
    if (!selectedEvento) return;
    const membresiaIds = Object.entries(asistenciaMap)
      .filter(([, checked]) => checked)
      .map(([id]) => id);
    setIsSavingAsistencia(true);
    try {
      await MinistriesService.registerAsistencia(selectedEvento.id, membresiaIds);
      showToast('Asistencia guardada', 'La asistencia fue registrada exitosamente.', 'success');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al guardar asistencia';
      showToast('Error', msg, 'error');
    } finally {
      setIsSavingAsistencia(false);
    }
  };

  const loadReporteAsistencia = async () => {
    if (!currentMinisterio?.id) return;
    setIsLoadingAsistenciaReporte(true);
    try {
      const data = await MinistriesService.getReporteAsistencia(
        currentMinisterio.id,
        asistenciaDesde || undefined,
        asistenciaHasta || undefined,
      );
      setAsistenciaReporte(data);
    } catch (error) {
      console.error('Error cargando reporte de asistencia:', error);
      showToast('Error', 'No se pudo cargar el reporte de asistencia.', 'error');
    } finally {
      setIsLoadingAsistenciaReporte(false);
    }
  };

  const loadMovimientos = async (eventoId: string) => {
    setIsLoadingMovimientos(true);
    try {
      const data = await MinistriesService.getMovimientos(eventoId);
      setMovimientosEvento(data);
    } catch (error) {
      console.error('Error cargando movimientos:', error);
      setMovimientosEvento([]);
    } finally {
      setIsLoadingMovimientos(false);
    }
  };

  const handleAddMovimiento = async () => {
    if (!selectedEvento) return;
    const monto = Number(nuevoMovimiento.monto);
    if (!monto || monto <= 0) {
      showToast('Monto inválido', 'Ingresa un monto mayor a cero.', 'warning');
      return;
    }
    setIsSavingMovimiento(true);
    try {
      await MinistriesService.addMovimiento(selectedEvento.id, {
        tipo: nuevoMovimiento.tipo,
        monto,
        descripcion: nuevoMovimiento.descripcion.trim() || undefined,
        fecha: nuevoMovimiento.fecha || undefined,
      });
      setNuevoMovimiento({ tipo: 'gasto', monto: '', descripcion: '', fecha: '' });
      await loadMovimientos(selectedEvento.id);
      showToast('Movimiento registrado', 'El movimiento fue agregado exitosamente.', 'success');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al registrar movimiento';
      showToast('Error', msg, 'error');
    } finally {
      setIsSavingMovimiento(false);
    }
  };

  const handleDeleteMovimiento = async (movimientoId: string) => {
    if (!selectedEvento) return;
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try {
      await MinistriesService.deleteMovimiento(movimientoId);
      await loadMovimientos(selectedEvento.id);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al eliminar movimiento';
      showToast('Error', msg, 'error');
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return value;
    const d = new Date(year, month - 1, day);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const isEventoPasado = (fecha?: string) => {
    if (!fecha) return false;
    const [year, month, day] = fecha.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return false;
    const eventoDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventoDate < today;
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startDayOfWeek = firstDayOfMonth.getDay(); // 0 = domingo

    const days: Array<{ date: number; fullDate: string; isCurrentMonth: boolean }> = [];

    // Días del mes anterior (padding)
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      days.push({ date: d, fullDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    // Días del mes actual
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: d, fullDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
    }

    // Días del mes siguiente (padding hasta completar 42 celdas = 6 semanas)
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      days.push({ date: d, fullDate: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    return days;
  };

  if (!isOpen || !currentMinisterio) return null;

  return (
    <>
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}

      <CreateEventoDrawer
        isOpen={isCreateEventoOpen}
        ministerioId={currentMinisterio.id}
        evento={editingEvento}
        onClose={() => {
          setIsCreateEventoOpen(false);
          setCreateEventoDate(undefined);
          setEditingEvento(null);
        }}
        defaultDate={createEventoDate}
        onSuccess={() => {
          refreshMinisterio();
        }}
      />

      <CreateMemberDrawer
        isOpen={isCreateMemberOpen}
        onClose={() => setIsCreateMemberOpen(false)}
        onSuccess={() => {
          refreshMinisterio();
          setIsCreateMemberOpen(false);
        }}
        ministerioId={currentMinisterio.id}
      />

      <div className="absolute inset-0 z-50 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={handleClose}
            aria-label="Cerrar drawer"
          />
          <section
            className={`absolute right-0 top-0 h-full w-full border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div
              className="h-full flex flex-col bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 border-b border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-xs sm:text-xs font-medium text-gray-900 truncate">{currentMinisterio.nombre}</h2>
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <button
                      onClick={() => setIsCreateMemberOpen(true)}
                      className="p-1.5 sm:px-3 sm:py-1.5 bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100 text-xs sm:text-xs font-medium flex items-center"
                      title="Crear miembro"
                    >
                      <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Crear Miembro</span>
                    </button>
                    {onEdit && (
                      <button
                        onClick={() => onEdit(currentMinisterio)}
                        className="p-1.5 sm:px-3 sm:py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-xs sm:text-xs font-medium flex items-center"
                        title="Editar"
                      >
                        <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Editar</span>
                      </button>
                    )}
                    {onToggleStatus && (
                      <button
                        onClick={async () => {
                          await onToggleStatus(currentMinisterio);
                          if (onRefresh) onRefresh();
                          // Recargar detalle
                          MinistriesService.getById(currentMinisterio.id)
                            .then((full) => setCurrentMinisterio(full))
                            .catch(() => {});
                        }}
                        className={`p-1.5 sm:px-3 sm:py-1.5 rounded-md text-xs sm:text-xs font-medium flex items-center ${
                          currentMinisterio.estado
                            ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                        title={currentMinisterio.estado ? 'Desactivar' : 'Activar'}
                      >
                        <Power className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">{currentMinisterio.estado ? 'Desactivar' : 'Activar'}</span>
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={async () => {
                          await onDelete(currentMinisterio.id, currentMinisterio.nombre);
                          handleClose();
                          if (onRefresh) onRefresh();
                        }}
                        className="p-1.5 sm:px-3 sm:py-1.5 bg-red-50 text-red-700 rounded-md hover:bg-red-100 text-xs sm:text-xs font-medium flex items-center"
                        title="Eliminar"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Eliminar</span>
                      </button>
                    )}
                    <button
                      onClick={handleClose}
                      className="text-gray-500 hover:text-gray-700 transition-colors ml-0.5 sm:ml-1"
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 sm:mt-4 border-b border-gray-200 overflow-x-auto lg:overflow-clip">
                  <nav className="-mb-px flex space-x-4 sm:space-x-8">
                    {(
                      [
                        { key: 'general' as TabType, label: 'General', icon: Info },
                        { key: 'integrantes' as TabType, label: 'Integrantes', icon: Users },
                        { key: 'eventos' as TabType, label: 'Eventos', icon: Calendar },
                        { key: 'programacion' as TabType, label: 'Programación', icon: CalendarDays },
                      ].concat(
                        canViewReporte ? [{ key: 'reporte' as TabType, label: 'Reporte', icon: FileText }] : []
                      )
                    ).map((tab) => {
                      const isActive = activeTab === tab.key;
                      const Icon = tab.icon;
                      const progBadge = tab.key === 'programacion' ? (asignacionesProg?.length ?? 0) : 0;
                      return (
                        <button
                          key={tab.key}
                          onClick={() => setActiveTab(tab.key)}
                          className={`group inline-flex items-center gap-1.5 sm:gap-2 py-2 sm:py-3 px-1 border-b-2 font-medium text-xs sm:text-xs transition-colors whitespace-nowrap ${
                            isActive
                              ? 'border-purple-600 text-purple-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                          {tab.label}
                          {progBadge > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-purple-600 text-white text-[10px] font-medium">
                              {progBadge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {activeTab === 'general' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                      <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                        <p className="text-xs sm:text-xs text-gray-500">Nombre</p>
                        <p className="text-xs sm:text-xs font-medium text-gray-900">{currentMinisterio.nombre}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                        <p className="text-xs sm:text-xs text-gray-500">Estado</p>
                        <span
                          className={`inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-xs font-medium ${
                            currentMinisterio.estado
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {currentMinisterio.estado ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                        <p className="text-xs sm:text-xs text-gray-500">Líder</p>
                        {currentMinisterio.lider ? (
                          <div className="mt-1">
                            <p className="text-xs sm:text-xs font-medium text-gray-900">
                              {currentMinisterio.lider.nombres} {currentMinisterio.lider.apellidos}
                            </p>
                            <p className="text-xs sm:text-xs text-gray-600">{currentMinisterio.lider.email}</p>
                          </div>
                        ) : (
                          <p className="text-xs sm:text-xs text-gray-900">Sin líder asignado</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                      <div className="bg-gray-50 rounded-lg p-4 sm:p-5 lg:col-span-2">
                        <p className="text-xs sm:text-xs text-gray-500 mb-1">Descripción</p>
                        <p className="text-xs sm:text-xs text-gray-900 whitespace-pre-line leading-relaxed">
                          {currentMinisterio.descripcion || 'Sin descripción'}
                        </p>
                      </div>
                      <div className="space-y-4 sm:space-y-6">
                        <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                          <p className="text-xs sm:text-xs text-gray-500 mb-1">Misión</p>
                          <p className="text-xs sm:text-xs text-gray-900 whitespace-pre-line leading-relaxed">
                            {currentMinisterio.mision || 'Sin misión'}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                          <p className="text-xs sm:text-xs text-gray-500 mb-1">Visión</p>
                          <p className="text-xs sm:text-xs text-gray-900 whitespace-pre-line leading-relaxed">
                            {currentMinisterio.vision || 'Sin visión'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                        <p className="text-xs sm:text-xs text-gray-500">Creado</p>
                        <p className="text-xs sm:text-xs text-gray-900">{formatDate(currentMinisterio.creadoEn)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 sm:p-5">
                        <p className="text-xs sm:text-xs text-gray-500">Actualizado</p>
                        <p className="text-xs sm:text-xs text-gray-900">{formatDate(currentMinisterio.actualizadoEn)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'integrantes' && (
                  <div className="space-y-4 sm:space-y-6">
                    <form onSubmit={handleAddIntegrante} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-xs sm:text-xs font-medium text-gray-900 mb-2 sm:mb-3">Agregar integrante</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
                        <div className="relative" ref={integranteSearchRef}>
                          {selectedIntegranteName ? (
                            <div className="flex items-center justify-between w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-purple-200 bg-purple-50 rounded-md">
                              <span className="text-xs sm:text-xs text-gray-900 font-medium truncate">{selectedIntegranteName}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setNewMembresiaId('');
                                  setSelectedIntegranteName('');
                                  setIntegranteSearch('');
                                }}
                                className="text-gray-400 hover:text-red-500 ml-2"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={integranteSearch}
                                  onChange={(e) => setIntegranteSearch(e.target.value)}
                                  onFocus={() => {
                                    if (integranteResults.length > 0) setShowIntegranteResults(true);
                                  }}
                                  placeholder="Buscar miembro..."
                                  className="w-full pl-8 pr-3 py-1.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-xs"
                                />
                                {integranteLoading && (
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                  </div>
                                )}
                              </div>
                              {showIntegranteResults && integranteResults.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                                  {integranteResults.map((miembro, idx) => (
                                    <button
                                      key={`${miembro.id}-${idx}`}
                                      type="button"
                                      onClick={() => {
                                        setNewMembresiaId(miembro.id);
                                        setSelectedIntegranteName(`${miembro.nombres} ${miembro.apellidos}`);
                                        setIntegranteSearch('');
                                        setShowIntegranteResults(false);
                                        setIntegranteResults([]);
                                      }}
                                      className="w-full px-3 py-2 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                    >
                                      <div className="text-xs font-medium text-gray-900">{miembro.nombres} {miembro.apellidos}</div>
                                      <div className="text-xs text-gray-500">{miembro.email}</div>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {showIntegranteResults && integranteSearch.length >= 2 && !integranteLoading && integranteResults.length === 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-500">
                                  No se encontraron miembros
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <input
                          type="text"
                          value={newRol}
                          onChange={(e) => setNewRol(e.target.value)}
                          placeholder="Rol (opcional)"
                          className="px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-xs"
                        />
                        <button
                          type="submit"
                          disabled={isSubmittingIntegrante || !newMembresiaId}
                          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center text-xs sm:text-xs"
                        >
                          <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Agregar
                        </button>
                      </div>
                    </form>

                    <div className="space-y-2 sm:space-y-3">
                      {(currentMinisterio.integrantes || []).length === 0 && (
                        <div className="text-center text-gray-500 py-8 text-xs">No hay integrantes registrados</div>
                      )}
                      {(currentMinisterio.integrantes || []).map((i, idx) => (
                        <div
                          key={`${i.id}-${idx}`}
                          className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 sm:p-4"
                        >
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-900">
                              {i.membresia.nombres} {i.membresia.apellidos}
                            </p>
                            <p className="text-xs sm:text-xs text-gray-500">{i.membresia.email}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                              {i.rol && <span className="px-1.5 sm:px-2 py-0.5 bg-purple-100 text-purple-700 rounded">{i.rol}</span>}
                              <span>Unido: {formatDate(i.fechaUnion)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveIntegrante(i.membresiaId)}
                            disabled={isRemovingIntegranteId === i.membresiaId}
                            className="text-red-600 hover:text-red-800 p-1.5 sm:p-2 rounded-md hover:bg-red-50 disabled:opacity-50 shrink-0"
                            title="Remover integrante"
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'eventos' && (
                  <div className="space-y-4 h-full flex flex-col">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xs sm:text-xs font-medium text-gray-900">
                          {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                        </h3>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                            className="p-1 sm:p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
                          >
                            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                          <button
                            onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                            className="p-1 sm:p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
                          >
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setCreateEventoDate(undefined);
                          setIsCreateEventoOpen(true);
                        }}
                        className="px-2.5 sm:px-3 py-1.5 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-xs sm:text-xs font-medium flex items-center justify-center"
                      >
                        <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Crear Evento</span>
                      </button>
                    </div>

                    {(currentMinisterio.eventos || []).length === 0 && (
                      <div className="text-center text-gray-500 py-8">No hay eventos registrados</div>
                    )}

                    {(currentMinisterio.eventos || []).length > 0 && (
                      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
                        {/* Calendario */}
                        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
                          {/* Header días */}
                          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                            {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((d) => (
                              <div key={d} className="py-1.5 sm:py-2 text-center text-xs sm:text-xs font-medium text-gray-600">
                                <span className="hidden sm:inline">{d}</span>
                                <span className="sm:hidden">{d.slice(0, 1)}</span>
                              </div>
                            ))}
                          </div>
                          {/* Celdas */}
                          <div className="grid grid-cols-7 flex-1 auto-rows-fr">
                            {getCalendarDays(calendarMonth).map((day, idx) => {
                              const dayEvents = eventosPorFecha[day.fullDate] || [];
                              const tieneProximo = dayEvents.some((ev) => ev.id === proximoEvento?.id);
                              return (
                                <div
                                  key={`cal-${day.fullDate}-${idx}`}
                                  onClick={() => {
                                    setCreateEventoDate(day.fullDate);
                                    setIsCreateEventoOpen(true);
                                  }}
                                  className={`min-h-[60px] sm:min-h-[100px] border-b border-r border-gray-100 p-1 sm:p-2 flex flex-col gap-0.5 sm:gap-1 cursor-pointer hover:bg-purple-50/30 transition-colors ${
                                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50/50'
                                  } ${idx % 7 === 6 ? 'border-r-0' : ''} ${
                                    tieneProximo ? 'ring-1 ring-inset ring-orange-300 bg-orange-50/40' : ''
                                  }`}
                                  title="Crear evento en este día"
                                >
                                  <span
                                    className={`self-start text-xs sm:text-xs font-medium w-5 h-5 sm:w-7 sm:h-7 flex items-center justify-center rounded-full ${
                                      day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                                    } ${tieneProximo ? 'bg-orange-100 text-orange-700' : ''}`}
                                  >
                                    {day.date}
                                  </span>
                                  <div className="flex flex-col gap-1 overflow-hidden">
                                    {dayEvents.slice(0, 2).map((ev, evIdx) => {
                                      const esProximo = ev.id === proximoEvento?.id;
                                      const pasado = isEventoPasado(ev.fecha);
                                      return (
                                        <button
                                          key={`${ev.id}-${evIdx}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedEvento(ev);
                                          }}
                                          className={`text-left text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded truncate transition-colors ${
                                            pasado
                                              ? 'bg-gray-200 text-gray-600 line-through decoration-gray-400'
                                              : esProximo
                                                ? 'bg-orange-100 text-orange-800 border border-orange-200 font-medium'
                                                : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                          }`}
                                          title={ev.titulo}
                                        >
                                          {ev.titulo}
                                        </button>
                                      );
                                    })}
                                    {dayEvents.length > 2 && (
                                      <span className="text-[10px] sm:text-xs text-gray-500 pl-0.5 sm:pl-1">
                                        +{dayEvents.length - 2} más
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Resumen mensual */}
                        <div className="lg:col-span-1 bg-gray-50 border border-gray-200 rounded-xl flex flex-col overflow-hidden">
                          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 bg-white">
                            <h4 className="font-medium text-gray-900 text-xs sm:text-xs">
                              Eventos de {monthNames[calendarMonth.getMonth()]}
                            </h4>
                            <p className="text-xs sm:text-xs text-gray-500">
                              {(() => {
                                const year = calendarMonth.getFullYear();
                                const month = calendarMonth.getMonth();
                                const eventosDelMes = (currentMinisterio.eventos || []).filter((ev) => {
                                  const d = parseFechaLocal(ev.fecha);
                                  return d.getFullYear() === year && d.getMonth() === month;
                                });
                                return `${eventosDelMes.length} evento${eventosDelMes.length !== 1 ? 's' : ''}`;
                              })()}
                            </p>
                          </div>
                          <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2">
                            {(() => {
                              const year = calendarMonth.getFullYear();
                              const month = calendarMonth.getMonth();
                              const eventosDelMes = (currentMinisterio.eventos || [])
                                .filter((ev) => {
                                  const d = parseFechaLocal(ev.fecha);
                                  return d.getFullYear() === year && d.getMonth() === month;
                                })
                                .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

                              if (eventosDelMes.length === 0) {
                                return (
                                  <div className="text-center text-gray-500 py-6 text-xs">
                                    No hay eventos este mes
                                  </div>
                                );
                              }

                              return eventosDelMes.map((ev, evIdx) => {
                                const esProximo = ev.id === proximoEvento?.id;
                                const pasado = isEventoPasado(ev.fecha);
                                return (
                                  <div
                                    key={`${ev.id}-${evIdx}`}
                                    className={`w-full border rounded-lg p-2 sm:p-3 transition-colors ${
                                      pasado
                                        ? 'bg-gray-100 border-gray-200 opacity-70'
                                        : esProximo
                                          ? 'bg-orange-50 border-orange-300'
                                          : 'bg-white border-gray-200'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <p className={`font-medium text-xs sm:text-xs ${pasado ? 'text-gray-500 line-through' : esProximo ? 'text-orange-900' : 'text-gray-900'}`}>
                                        {ev.titulo}
                                      </p>
                                      {esProximo && (
                                        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                                          Próximo
                                        </span>
                                      )}
                                      {pasado && (
                                        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded">
                                          Pasado
                                        </span>
                                      )}
                                    </div>
                                    <p className={`text-[10px] sm:text-xs ${pasado ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(ev.fecha)}</p>
                                    <button
                                      onClick={() => setSelectedEvento(ev)}
                                      className={`mt-1.5 sm:mt-2 text-[10px] sm:text-xs font-medium hover:underline ${pasado ? 'text-gray-500 hover:text-gray-700' : 'text-purple-700 hover:text-purple-900'}`}
                                    >
                                      Ver detalle →
                                    </button>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'programacion' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-gray-900">
                        Actividades asignadas
                        {asignacionesProg && asignacionesProg.length > 0 && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">
                            {asignacionesProg.length} próxima(s)
                          </span>
                        )}
                      </p>
                    </div>
                    {asignacionesProg === null ? (
                      <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-600 border-t-transparent" />
                      </div>
                    ) : asignacionesProg.length === 0 ? (
                      <p className="text-xs text-gray-400 py-10 text-center">
                        No hay actividades de programación semanal próximas para este ministerio.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {asignacionesProg.map((a) => (
                          <div key={a.id} className="border border-gray-200 rounded-xl p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 capitalize">
                                  {formatFechaLocal(a.fecha, { weekday: 'long', day: 'numeric', month: 'long' })}
                                </p>
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                  {a.horaInicio} – {a.horaFin}
                                  {a.programacion && (
                                    <span className="text-gray-400">
                                      {' · Semana '}{formatFechaLocal(a.programacion.fechaInicio, { day: 'numeric', month: 'short' })}
                                      {' – '}{formatFechaLocal(a.programacion.fechaFin, { day: 'numeric', month: 'short' })}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <EstadoSeguimientoBadge estado={a.estado} />
                                {(a.actividad || a.rol) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">
                                    {a.actividad ?? a.rol}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 pt-3 border-t border-gray-100">
                              <p className="text-[11px]">
                                <span className="text-gray-400">Participante: </span>
                                <span className="text-gray-700">{a.participante?.nombreCompleto ?? '—'}</span>
                              </p>
                              <p className="text-[11px]">
                                <span className="text-gray-400">Líder a cargo: </span>
                                <span className="text-gray-700">{a.lider?.nombreCompleto ?? '—'}</span>
                              </p>
                              {a.rol && a.actividad && (
                                <p className="text-[11px]">
                                  <span className="text-gray-400">Rol: </span>
                                  <span className="text-gray-700">{a.rol}</span>
                                </p>
                              )}
                              {a.observaciones && (
                                <p className="text-[11px] sm:col-span-2">
                                  <span className="text-gray-400">Observaciones: </span>
                                  <span className="text-gray-700">{a.observaciones}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'reporte' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex border-b border-gray-200 gap-1 sm:gap-2">
                      <button
                        onClick={() => setReporteSubTab('registros')}
                        className={`py-2 px-2 sm:px-3 text-xs font-medium border-b-2 -mb-px transition-colors ${
                          reporteSubTab === 'registros'
                            ? 'border-purple-600 text-purple-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Registros
                      </button>
                      <button
                        onClick={() => setReporteSubTab('asistencia')}
                        className={`py-2 px-2 sm:px-3 text-xs font-medium border-b-2 -mb-px transition-colors ${
                          reporteSubTab === 'asistencia'
                            ? 'border-purple-600 text-purple-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Asistencia
                      </button>
                    </div>

                    {reporteSubTab === 'registros' && (
                    <>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <h3 className="text-xs sm:text-xs font-medium text-gray-900">Reporte de registros</h3>
                      <button
                        onClick={() => {
                          if (!miembrosRegistrados || miembrosRegistrados.length === 0) return;
                          const doc = new jsPDF();
                          doc.setFontSize(16);
                          doc.text(`Reporte de registros - ${currentMinisterio?.nombre || 'Ministerio'}`, 14, 20);
                          doc.setFontSize(11);
                          doc.text(`Fecha: ${new Date().toLocaleDateString('es-CO')}`, 14, 28);
                          const body: Array<string[]> = [];
                          miembrosRegistrados.forEach((r) => {
                            body.push([r.nombre, '', '']);
                            r.miembros.forEach((m) => {
                              body.push(['', m.nombreCompleto, new Date(m.creadoEn).toLocaleDateString('es-CO')]);
                            });
                          });
                          autoTable(doc, {
                            startY: 35,
                            head: [['Registrador', 'Miembro', 'Fecha inscripción']],
                            body,
                            theme: 'striped',
                            headStyles: { fillColor: [124, 58, 237] },
                          });
                          const total = miembrosRegistrados.reduce((sum, r) => sum + r.count, 0);
                          const finalY = (doc as any).lastAutoTable?.finalY || 50;
                          doc.setFontSize(12);
                          doc.text(`Total de miembros registrados: ${total}`, 14, finalY + 10);
                          doc.save(`reporte-${currentMinisterio?.nombre || 'ministerio'}.pdf`);
                        }}
                        disabled={!miembrosRegistrados || miembrosRegistrados.length === 0}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-xs sm:text-xs"
                      >
                        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Exportar PDF
                      </button>
                    </div>

                    {!miembrosRegistrados && (
                      <div className="text-center text-gray-500 py-8 text-xs">Cargando datos...</div>
                    )}

                    {miembrosRegistrados && miembrosRegistrados.length === 0 && (
                      <div className="text-center text-gray-500 py-8 text-xs">No hay registros de miembros para este ministerio.</div>
                    )}

                    {miembrosRegistrados && miembrosRegistrados.length > 0 && (
                      <div className="space-y-4 sm:space-y-6">
                        {miembrosRegistrados.map((reg, idx) => (
                          <div key={`${reg.creadoPorId}-${idx}`} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-purple-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-medium text-[10px] sm:text-xs">
                                  {reg.nombre.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>
                                <span className="text-xs sm:text-xs font-medium text-purple-900">{reg.nombre}</span>
                              </div>
                              <span className="text-xs sm:text-xs font-medium text-purple-700">{reg.count} miembros</span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-100">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Miembro</th>
                                    <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Fecha inscripción</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {reg.miembros.map((m) => (
                                    <tr key={m.id}>
                                      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-xs text-gray-900">{m.nombreCompleto}</td>
                                      <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-xs text-gray-600 text-right">
                                        {new Date(m.creadoEn).toLocaleDateString('es-CO', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                        })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}

                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4 flex items-center justify-between">
                          <p className="text-xs sm:text-xs font-medium text-purple-800">Total de miembros registrados por este ministerio</p>
                          <p className="text-xs sm:text-xs font-medium text-purple-800">
                            {miembrosRegistrados.reduce((sum, r) => sum + r.count, 0)}
                          </p>
                        </div>
                      </div>
                    )}
                    </>
                    )}

                    {reporteSubTab === 'asistencia' && (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
                          <div>
                            <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">Desde</label>
                            <input
                              type="date"
                              value={asistenciaDesde}
                              onChange={(e) => setAsistenciaDesde(e.target.value)}
                              className="px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">Hasta</label>
                            <input
                              type="date"
                              value={asistenciaHasta}
                              onChange={(e) => setAsistenciaHasta(e.target.value)}
                              className="px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs"
                            />
                          </div>
                          <button
                            onClick={loadReporteAsistencia}
                            disabled={isLoadingAsistenciaReporte}
                            className="px-3 sm:px-4 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-xs font-medium"
                          >
                            {isLoadingAsistenciaReporte ? 'Generando...' : 'Generar reporte'}
                          </button>
                        </div>

                        {!asistenciaReporte && !isLoadingAsistenciaReporte && (
                          <p className="text-xs text-gray-400 text-center py-8">
                            Selecciona un rango de fechas (opcional) y genera el reporte para ver el compromiso de asistencia por integrante.
                          </p>
                        )}

                        {asistenciaReporte && (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-purple-50 rounded-lg p-3">
                                <p className="text-[10px] text-purple-600">Eventos en el período</p>
                                <p className="text-lg font-semibold text-purple-900">{asistenciaReporte.totalEventos}</p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] text-gray-500">Integrantes evaluados</p>
                                <p className="text-lg font-semibold text-gray-900">{asistenciaReporte.miembros.length}</p>
                              </div>
                            </div>

                            {asistenciaReporte.totalEventos === 0 ? (
                              <p className="text-xs text-gray-400 text-center py-6">No hay eventos en el rango seleccionado.</p>
                            ) : asistenciaReporte.miembros.length === 0 ? (
                              <p className="text-xs text-gray-400 text-center py-6">Este ministerio no tiene integrantes.</p>
                            ) : (
                              <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-100">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Integrante</th>
                                      <th className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase">Asist.</th>
                                      <th className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase">Inasist.</th>
                                      <th className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase hidden sm:table-cell">Sin registro</th>
                                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-32">%</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {asistenciaReporte.miembros.map((m) => (
                                      <tr key={m.membresiaId}>
                                        <td className="px-2 sm:px-3 py-2">
                                          <p className="text-xs font-medium text-gray-900">{m.nombres} {m.apellidos}</p>
                                          {m.rol && <p className="text-[10px] text-purple-600">{m.rol}</p>}
                                        </td>
                                        <td className="px-2 py-2 text-center text-xs text-green-700 font-medium">{m.asistencias}</td>
                                        <td className="px-2 py-2 text-center text-xs text-red-600 font-medium">{m.inasistencias}</td>
                                        <td className="px-2 py-2 text-center text-xs text-gray-400 hidden sm:table-cell">{m.sinRegistro}</td>
                                        <td className="px-2 sm:px-3 py-2">
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                              <div
                                                className={`h-full rounded-full ${m.porcentaje >= 70 ? 'bg-green-500' : m.porcentaje >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                style={{ width: `${m.porcentaje}%` }}
                                              />
                                            </div>
                                            <span className="text-[10px] text-gray-600 w-8 text-right">{m.porcentaje}%</span>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {selectedEvento && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedEvento(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-xs sm:text-xs font-medium text-gray-900 truncate">{selectedEvento.titulo}</h3>
              <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                {!isEventoPasado(selectedEvento.fecha) && (
                  <button
                    onClick={() => {
                      setEditingEvento(selectedEvento);
                      setSelectedEvento(null); // cerrar modal del evento
                      setIsCreateEventoOpen(true);
                    }}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-xs sm:text-xs font-medium flex items-center gap-1"
                    title="Editar evento"
                  >
                    <Edit2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    <span className="hidden sm:inline">Editar</span>
                  </button>
                )}
                <button
                  onClick={() => setSelectedEvento(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="bg-gray-50 rounded-md p-2.5 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500">Fecha</p>
                  <p className="text-xs sm:text-xs font-medium text-gray-900">{formatDate(selectedEvento.fecha)}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-2.5 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500">Lugar</p>
                  <p className="text-xs sm:text-xs font-medium text-gray-900">{selectedEvento.lugar || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-2.5 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500">Hora inicio</p>
                  <p className="text-xs sm:text-xs font-medium text-gray-900">{selectedEvento.horaInicio || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-2.5 sm:p-3">
                  <p className="text-[10px] sm:text-xs text-gray-500">Hora fin</p>
                  <p className="text-xs sm:text-xs font-medium text-gray-900">{selectedEvento.horaFin || '-'}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-2.5 sm:p-3">
                <p className="text-[10px] sm:text-xs text-gray-500">Descripción</p>
                <p className="text-xs sm:text-xs text-gray-900 whitespace-pre-line">
                  {selectedEvento.descripcion || 'Sin descripción'}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-3 sm:pt-4">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <h4 className="text-xs sm:text-xs font-medium text-gray-900">Registrar asistencia</h4>
                {isLoadingAsistencia && (
                  <svg className="animate-spin h-3.5 w-3.5 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
              </div>
              <div className="space-y-1.5 sm:space-y-2 max-h-64 overflow-y-auto pr-1">
                {(currentMinisterio?.integrantes || []).length === 0 && (
                  <p className="text-xs sm:text-xs text-gray-500">No hay integrantes en este ministerio.</p>
                )}
                {(currentMinisterio?.integrantes || []).map((int, idx) => (
                  <label
                    key={`${int.membresiaId}-${idx}`}
                    className={`flex items-center space-x-2 sm:space-x-3 p-1.5 sm:p-2 rounded-md hover:bg-gray-50 ${isLoadingAsistencia ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      checked={!!asistenciaMap[int.membresiaId]}
                      onChange={() => handleToggleAsistencia(int.membresiaId)}
                      disabled={isLoadingAsistencia}
                    />
                    <span className="text-xs sm:text-xs text-gray-900">
                      {int.membresia.nombres} {int.membresia.apellidos}
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-3 sm:mt-4 flex justify-end">
                <button
                  onClick={handleSaveAsistencia}
                  disabled={isSavingAsistencia || isLoadingAsistencia}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center text-xs sm:text-xs"
                >
                  {isSavingAsistencia && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Guardar asistencia
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-3 sm:pt-4">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <h4 className="text-xs sm:text-xs font-medium text-gray-900">Finanzas del evento</h4>
                {isLoadingMovimientos && (
                  <svg className="animate-spin h-3.5 w-3.5 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
              </div>

              {(() => {
                const totalIngresos = movimientosEvento
                  .filter((m) => m.tipo === 'ingreso')
                  .reduce((sum, m) => sum + Number(m.monto), 0);
                const totalGastos = movimientosEvento
                  .filter((m) => m.tipo === 'gasto')
                  .reduce((sum, m) => sum + Number(m.monto), 0);
                const balance = totalIngresos - totalGastos;
                const presupuesto = selectedEvento?.presupuesto != null ? Number(selectedEvento.presupuesto) : null;
                const fmt = (v: number) =>
                  v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
                return (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {presupuesto != null && (
                        <div className="bg-purple-50 rounded-md p-2">
                          <p className="text-[10px] text-purple-600">Presupuesto</p>
                          <p className="text-xs font-semibold text-purple-900">{fmt(presupuesto)}</p>
                        </div>
                      )}
                      <div className="bg-green-50 rounded-md p-2">
                        <p className="text-[10px] text-green-700">Ingresos</p>
                        <p className="text-xs font-semibold text-green-900">{fmt(totalIngresos)}</p>
                      </div>
                      <div className="bg-red-50 rounded-md p-2">
                        <p className="text-[10px] text-red-700">Gastos</p>
                        <p className="text-xs font-semibold text-red-900">{fmt(totalGastos)}</p>
                      </div>
                      <div className={`rounded-md p-2 ${balance >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                        <p className={`text-[10px] ${balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>Balance</p>
                        <p className={`text-xs font-semibold ${balance >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>{fmt(balance)}</p>
                      </div>
                    </div>

                    {presupuesto != null && presupuesto > 0 && (
                      <div>
                        <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                          <span>Ejecutado sobre presupuesto</span>
                          <span>{Math.round((totalGastos / presupuesto) * 100)}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${totalGastos > presupuesto ? 'bg-red-500' : 'bg-purple-500'}`}
                            style={{ width: `${Math.min(100, (totalGastos / presupuesto) * 100)}%` }}
                          />
                        </div>
                        {totalGastos > presupuesto && (
                          <p className="text-[10px] text-red-600 mt-1">
                            Los gastos superan el presupuesto por {fmt(totalGastos - presupuesto)}
                          </p>
                        )}
                      </div>
                    )}

                    {movimientosEvento.length > 0 && (
                      <div className="border border-gray-100 rounded-md divide-y divide-gray-100 max-h-40 overflow-y-auto">
                        {movimientosEvento.map((mov) => (
                          <div key={mov.id} className="flex items-center justify-between px-2.5 py-1.5">
                            <div className="min-w-0">
                              <p className="text-xs text-gray-900 truncate">
                                {mov.descripcion || (mov.tipo === 'ingreso' ? 'Ingreso' : 'Gasto')}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(mov.fecha).toLocaleDateString('es-CO')}
                                {mov.creador ? ` · ${mov.creador.nombreCompleto}` : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-xs font-medium ${mov.tipo === 'ingreso' ? 'text-green-700' : 'text-red-600'}`}>
                                {mov.tipo === 'ingreso' ? '+' : '-'}{Number(mov.monto).toLocaleString('es-CO')}
                              </span>
                              {canEditEventos && (
                                <button
                                  onClick={() => handleDeleteMovimiento(mov.id)}
                                  className="text-gray-300 hover:text-red-500 p-0.5"
                                  title="Eliminar movimiento"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {canEditEventos && (
                      <div className="flex flex-wrap items-end gap-2 bg-gray-50 rounded-md p-2">
                        <select
                          value={nuevoMovimiento.tipo}
                          onChange={(e) => setNuevoMovimiento((prev) => ({ ...prev, tipo: e.target.value as 'ingreso' | 'gasto' }))}
                          className="px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="gasto">Gasto</option>
                          <option value="ingreso">Ingreso</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="Monto"
                          value={nuevoMovimiento.monto}
                          onChange={(e) => setNuevoMovimiento((prev) => ({ ...prev, monto: e.target.value }))}
                          className="w-24 px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          placeholder="Descripción"
                          value={nuevoMovimiento.descripcion}
                          onChange={(e) => setNuevoMovimiento((prev) => ({ ...prev, descripcion: e.target.value }))}
                          className="flex-1 min-w-[120px] px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="date"
                          value={nuevoMovimiento.fecha}
                          onChange={(e) => setNuevoMovimiento((prev) => ({ ...prev, fecha: e.target.value }))}
                          className="px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <button
                          onClick={handleAddMovimiento}
                          disabled={isSavingMovimiento || !nuevoMovimiento.monto}
                          className="px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-xs font-medium"
                        >
                          Agregar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

        </div>
      )}
    </>
  );
};
